import { createContext, useCallback, useContext, useEffect, useState, type ReactNode } from 'react'
import {
  type User,
  type UserCredential,
  type AuthProvider as FirebaseAuthProvider,
  browserLocalPersistence,
  browserSessionPersistence,
  confirmPasswordReset,
  createUserWithEmailAndPassword,
  fetchSignInMethodsForEmail,
  getRedirectResult,
  GithubAuthProvider,
  GoogleAuthProvider,
  linkWithCredential,
  linkWithPopup,
  onAuthStateChanged,
  reauthenticateWithPopup,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut,
} from 'firebase/auth'
import { auth, githubProvider, githubRepositoryProvider, googleProvider } from './firebase'
import { getUserData, invalidateUserData } from './userData'
import { safeNextPath } from './authNext'
import { identify, track } from './th'

type AuthError = Error & { code?: string }

// A redirect-based OAuth sign-in (popup-blocked fallback) bounces the whole page
// through the provider and back. We stash the intended destination before leaving
// so we can finish the session exchange and land the user correctly on return —
// even on browsers where getRedirectResult() comes back null (mobile Safari, the
// GitHub in-app browser) but onAuthStateChanged still delivers the user.
const REDIRECT_PENDING_KEY = 'appnz:auth:redirect-next'
const REDIRECT_ERROR_KEY = 'appnz:auth:redirect-error'

function markRedirectPending(next: string) {
  try {
    sessionStorage.setItem(REDIRECT_PENDING_KEY, safeNextPath(next))
  } catch {
    // sessionStorage can throw in locked-down/incognito contexts; the redirect
    // still works, we just fall back to the ?next query param on return.
  }
}

function authError(message: string, code: string): AuthError {
  const err = new Error(message) as AuthError
  err.code = code
  return err
}

async function readJSON(response: Response): Promise<Record<string, unknown>> {
  return response.json().catch(() => ({}))
}

// Server account behind a session: the opaque app.nz user id, and whether this
// sign-in created it (drives sign_up_completed vs sign_in_completed).
type SessionAccount = { id: string; created: boolean }

function sessionAccount(body: Record<string, unknown>, created: boolean): SessionAccount {
  const user = body.user as { id?: unknown } | undefined
  return { id: typeof user?.id === 'string' ? user.id : '', created }
}

function authCompleted(method: string, account: SessionAccount) {
  if (account.id) identify(account.id)
  track(account.created ? 'sign_up_completed' : 'sign_in_completed', { method })
}

function oauthStarted(method: string) {
  const register = /^\/(signup|register)\b/.test(window.location.pathname) || new URLSearchParams(window.location.search).get('mode') === 'register'
  track(register ? 'sign_up_started' : 'sign_in_started', { method })
}

function providerMethod(user: User): string {
  return (user.providerData[0]?.providerId || 'oauth').replace(/\.com$/, '')
}

async function legacyPasswordAuth(endpoint: '/api/login' | '/api/register', email: string, password: string): Promise<SessionAccount> {
  const response = await fetch(endpoint, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, password }),
  })

  const body = await readJSON(response)
  if (response.ok) return sessionAccount(body, endpoint === '/api/register')

  const message = typeof body.error === 'string' ? body.error : 'Authentication failed'
  if (response.status === 401) throw authError(message, 'legacy/invalid-credentials')
  if (response.status === 409) throw authError(message, 'legacy/email-already-in-use')
  throw authError(message, `legacy/${response.status}`)
}

function canUseLegacyPasswordFallback(err: unknown): boolean {
  const code = (err as { code?: string })?.code || ''
  return (
    code === 'auth/operation-not-allowed' ||
    code === 'auth/admin-restricted-operation' ||
    code === 'auth/configuration-not-found' ||
    code === 'auth/auth-domain-config-required' ||
    code === 'auth/unavailable' ||
    // Firebase unreachable (offline dev boxes, locked-down CI/e2e networks):
    // the local password endpoints still work, so don't strand the user.
    code === 'auth/network-request-failed' ||
    code === 'auth/internal-error' ||
    code === 'firebase-session/503'
  )
}

// Trade a Firebase ID token for an app.nz server session cookie. Every sign-in
// path (email, Google, GitHub) funnels through here so the existing cookie-based
// endpoints (/api/me, billing, API keys) keep working unchanged.
async function exchangeSession(user: User): Promise<SessionAccount> {
  const idToken = await user.getIdToken()
  const res = await fetch('/api/firebase-session', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ idToken }),
  })
  const body = await readJSON(res)
  if (!res.ok) {
    const err = new Error(typeof body.error === 'string' ? body.error : 'Could not establish your session') as AuthError
    err.code = `firebase-session/${res.status}`
    throw err
  }
  return sessionAccount(body, body.created === true)
}

// Firebase local persistence outlives the server session cookie (expiry,
// cleared cookies, session-store loss), leaving the UI "signed in" while API
// calls 401. On load, verify the cookie and silently re-mint it if stale.
async function ensureServerSession(user: User): Promise<void> {
  try {
    const res = await fetch('/api/me', { credentials: 'include' })
    const body = await readJSON(res)
    if (!body?.authenticated) await exchangeSession(user)
  } catch {
    // Offline or Firebase unreachable: leave things as they are.
  }
  invalidateUserData()
  void getUserData(true)
}

// Firebase enforces one-account-per-email by default. If a user signs in with a
// provider whose email already belongs to a different provider, link the new
// credential onto the existing account so Google + GitHub merge into one user.
async function handleAccountExistsConflict(
  attempted: 'github' | 'google',
  err: { customData?: { email?: string } },
): Promise<UserCredential> {
  if (!auth) throw err
  const email = err?.customData?.email
  const pendingCred =
    attempted === 'github'
      ? GithubAuthProvider.credentialFromError(err as never)
      : GoogleAuthProvider.credentialFromError(err as never)
  if (!email || !pendingCred) throw err

  let methods: string[] = []
  try {
    methods = await fetchSignInMethodsForEmail(auth, email)
  } catch {
    // Email-enumeration protection can hide methods; fall through to the
    // deterministic guess below (only Google + GitHub are configured).
  }

  if (methods.includes('password')) {
    const e = new Error(
      `An account already exists for ${email} with an email/password login. Sign in with your email and password first.`,
    ) as Error & { code?: string }
    e.code = 'auth/account-exists-with-different-credential'
    throw e
  }

  let existing: FirebaseAuthProvider
  if (methods.includes(GoogleAuthProvider.PROVIDER_ID)) existing = googleProvider
  else if (methods.includes(GithubAuthProvider.PROVIDER_ID)) existing = githubProvider
  else existing = attempted === 'github' ? googleProvider : githubProvider

  const result = await signInWithPopup(auth, existing)
  return linkWithCredential(result.user, pendingCred)
}

interface AuthContextType {
  user: User | null
  loading: boolean
  signInWithGoogle: () => Promise<void>
  signInWithGithub: () => Promise<void>
  connectGithub: () => Promise<void>
  signInWithEmail: (email: string, password: string) => Promise<void>
  signUpWithEmail: (email: string, password: string) => Promise<void>
  sendPasswordReset: (email: string) => Promise<void>
  confirmReset: (oobCode: string, newPassword: string) => Promise<void>
  logout: () => Promise<void>
  getIdToken: () => Promise<string | null>
}

// Store the short-lived OAuth credential server-side as the user's GitHub
// provider key. The agent worker reads this key when it needs to clone, push,
// or open a PR against a GitHub repository; it never needs to receive the
// browser's Firebase credential directly.
async function saveGithubCredential(accessToken: string): Promise<void> {
  const response = await fetch('/api/gateway/provider-keys', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ provider: 'github', api_key: accessToken }),
  })
  if (!response.ok) {
    const body = await readJSON(response)
    throw new Error(typeof body.error === 'string' ? body.error : 'Could not save the GitHub connection')
  }
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const a = auth
    if (!a) {
      setLoading(false)
      return
    }
    setPersistence(a, browserLocalPersistence).catch(() => {
      setPersistence(a, browserSessionPersistence).catch(() => {})
    })

    // Finish a redirect-based sign-in (the popup-blocked fallback below): trade the
    // returned Firebase user for the server session cookie, then send them on to
    // where the popup path would have. Guarded so it runs at most once and only
    // when a redirect was actually pending — a normal authenticated page load must
    // not hijack navigation.
    let finishing = false
    const finishRedirect = async (u: User) => {
      // Gate strictly on the flag set right before signInWithRedirect. It lives in
      // first-party sessionStorage (which works even on the browsers where
      // getRedirectResult returns null), so it reliably marks a redirect return
      // without hijacking navigation on a normal already-signed-in page load.
      let pending: string | null = null
      try {
        pending = sessionStorage.getItem(REDIRECT_PENDING_KEY)
      } catch {
        pending = null
      }
      if (pending === null || finishing) return
      finishing = true
      try { sessionStorage.removeItem(REDIRECT_PENDING_KEY) } catch {}
      try {
        authCompleted(providerMethod(u), await exchangeSession(u))
        window.location.href = pending || '/account'
      } catch (err) {
        finishing = false
        const message = (err as Error)?.message || 'Could not finish sign-in. Please try again.'
        try { sessionStorage.setItem(REDIRECT_ERROR_KEY, message) } catch {}
      }
    }

    // getRedirectResult resolves the pending credential on most browsers (and runs
    // account-linking). On mobile Safari / the GitHub in-app browser it can resolve
    // null even on a successful return, so onAuthStateChanged below is the safety
    // net: it still delivers the user, and finishRedirect only acts when a redirect
    // was actually pending.
    getRedirectResult(a)
      .then((result) => {
        if (result?.user) return finishRedirect(result.user)
      })
      .catch(() => {})

    let ensured = false
    return onAuthStateChanged(a, (next) => {
      setUser(next)
      setLoading(false)
      if (next) {
        void finishRedirect(next)
        if (!ensured && !finishing) {
          ensured = true
          void ensureServerSession(next)
        }
      }
    })
  }, [])

  const afterSignIn = useCallback(async (cred: UserCredential, method: string) => {
    authCompleted(method, await exchangeSession(cred.user))
  }, [])

  const signInWithProvider = useCallback(
    async (provider: FirebaseAuthProvider, name: 'github' | 'google') => {
      if (!auth) throw new Error('Sign-in is unavailable right now.')
      oauthStarted(name)
      try {
        const result = await signInWithPopup(auth, provider)
        await afterSignIn(result, name)
      } catch (err: unknown) {
        const code = (err as { code?: string })?.code || ''
        if (code === 'auth/account-exists-with-different-credential') {
          const linked = await handleAccountExistsConflict(name, err as { customData?: { email?: string } })
          await afterSignIn(linked, name)
          return
        }
        if (code === 'auth/popup-blocked' || code === 'auth/operation-not-supported-in-this-environment') {
          // Popups are blocked (common on mobile Safari / strict browsers): fall
          // back to a full-page redirect. On return, the AuthProvider effect
          // finishes the session exchange and navigates to this stored target.
          const params = new URLSearchParams(window.location.search)
          markRedirectPending(params.get('next') || '')
          await signInWithRedirect(auth, provider)
          return
        }
        if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
          return // user dismissed; not an error
        }
        throw err
      }
    },
    [afterSignIn],
  )

  const signInWithGoogle = useCallback(() => signInWithProvider(googleProvider, 'google'), [signInWithProvider])
  const signInWithGithub = useCallback(() => signInWithProvider(githubProvider, 'github'), [signInWithProvider])

  const connectGithub = useCallback(async () => {
    if (!auth || !user) throw new Error('Sign in with Firebase before connecting GitHub.')

    // A GitHub identity may already be linked to this Firebase user. Reauth
    // obtains a fresh OAuth access token in that case; otherwise link the new
    // identity and keep the account's existing sign-in method intact.
    const alreadyLinked = user.providerData.some((provider) => provider.providerId === GithubAuthProvider.PROVIDER_ID)
	const result = alreadyLinked
	  ? await reauthenticateWithPopup(user, githubRepositoryProvider)
	  : await linkWithPopup(user, githubRepositoryProvider)
    const credential = GithubAuthProvider.credentialFromResult(result)
    const accessToken = credential?.accessToken
    if (!accessToken) throw new Error('GitHub did not return an access token.')
    await exchangeSession(result.user)
    await saveGithubCredential(accessToken)
  }, [user])

  const signInWithEmail = useCallback(async (email: string, password: string) => {
    track('sign_in_started', { method: 'email' })
    if (!auth) {
      authCompleted('email', await legacyPasswordAuth('/api/login', email, password))
      return
    }

    let account: SessionAccount
    try {
      const cred = await signInWithEmailAndPassword(auth, email, password)
      account = await exchangeSession(cred.user)
    } catch (err) {
      if (!canUseLegacyPasswordFallback(err)) throw err
      account = await legacyPasswordAuth('/api/login', email, password)
    }
    authCompleted('email', account)
  }, [])

  const signUpWithEmail = useCallback(async (email: string, password: string) => {
    track('sign_up_started', { method: 'email' })
    if (!auth) {
      authCompleted('email', await legacyPasswordAuth('/api/register', email, password))
      return
    }

    let account: SessionAccount
    try {
      const cred = await createUserWithEmailAndPassword(auth, email, password)
      account = await exchangeSession(cred.user)
    } catch (err) {
      if (!canUseLegacyPasswordFallback(err)) throw err
      account = await legacyPasswordAuth('/api/register', email, password)
    }
    authCompleted('email', account)
  }, [])

  const sendPasswordReset = useCallback(async (email: string) => {
    // Always send via our own server (AWS SES, from lee.penkman@netwrck.com) so a
    // real email goes out and sets a local password_hash usable with /api/login.
    // Firebase's sendPasswordResetEmail is a silent no-op for Google-only accounts
    // (email-enumeration protection), so we don't route resets through it.
    const res = await fetch('/api/forgot-password', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    })
    if (!res.ok) throw new Error('Could not send reset email right now.')
  }, [])

  // Completes a reset using the oobCode from a Firebase reset-email link, for
  // the in-app /reset-password page (when the Firebase action URL points here).
  const confirmReset = useCallback(async (oobCode: string, newPassword: string) => {
    if (!auth) throw new Error('Password reset is unavailable right now.')
    await confirmPasswordReset(auth, oobCode, newPassword)
  }, [])

  const logout = useCallback(async () => {
    try {
      await fetch('/api/logout', { method: 'POST' })
    } catch {
      // best-effort; still clear Firebase state below
    }
    if (auth) await signOut(auth)
    identify(null)
    invalidateUserData()
    void getUserData(true)
  }, [])

  const getIdToken = useCallback(async () => {
    if (!user) return null
    try {
      return await user.getIdToken()
    } catch {
      return null
    }
  }, [user])

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        signInWithGoogle,
        signInWithGithub,
        connectGithub,
        signInWithEmail,
        signUpWithEmail,
        sendPasswordReset,
        confirmReset,
        logout,
        getIdToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
